import { Check, Image, Play, Video } from "lucide-react";
import type { Asset } from "../../lib/api.ts";
import { pressHandlers } from "../../lib/longpress.ts";
import { Pill, StatusPill, type LadderState, type Tone } from "../../ui/index.ts";

/**
 * The trip's recordings as square tiles. Nothing else: the tab owns which day is shown, who is
 * switched on and what is selected, so this file is only "draw these, report what was touched".
 */

/**
 * What a tile says about an original that is not safely stored yet. The ladder words are used
 * wherever the state is a ladder state; the rest keep the library's existing wording. A VERIFIED
 * original says nothing at all — the picture is the news, not its plumbing.
 */
type TileStatus = { ladder: LadderState | "FAILED" } | { label: string; tone: Tone };
const STATUS_OF: Record<string, TileStatus> = {
  QUEUED: { ladder: "QUEUED" },
  UPLOADING: { ladder: "UPLOADING" },
  STORED_UNVERIFIED: { ladder: "AWAITING_VERIFICATION" },
  FAILED: { ladder: "FAILED" },
  FINALIZING: { label: "Finishing…", tone: "wait" },
  PAUSED: { label: "Paused", tone: "wait" },
  NOT_UPLOADED: { label: "Not uploaded yet", tone: "neutral" },
};

const timeOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "no time";

/**
 * What a screen reader hears. "No preview available" comes from the server's own answer about the
 * rendition, not from whether its URL has arrived yet: a tile that is still fetching one is
 * loading, and saying it has no preview would be wrong for the second it takes.
 */
function describeTile(a: Asset): string {
  const kind = a.kind === "VIDEO" ? "Video" : "Photo";
  const who = a.own ? "you" : (a.contributorName ?? "a traveller");
  return `${kind} by ${who} at ${timeOf(a.captureAt)}${a.thumbReady ? "" : ", no preview available"}`;
}

export function MediaGrid({
  assets,
  thumbs,
  selected,
  selecting,
  onOpen,
  onToggle,
}: {
  assets: Asset[];
  thumbs: Record<string, string>;
  selected: Set<string>;
  /** True once anything is selected: every tile shows its toggle and a tap selects. */
  selecting: boolean;
  onOpen: (asset: Asset) => void;
  onToggle: (assetId: string) => void;
}) {
  return (
    <ul className="tile-grid media-grid plain" aria-label="Recordings">
      {assets.map((a) => {
        const url = thumbs[a.id];
        const isSelected = selected.has(a.id);
        const label = describeTile(a);
        // A tap opens the recording; once anything is selected a tap adds to the selection, so a
        // second one never needs a long press of its own.
        const press = pressHandlers({
          onPress: () => (selecting ? onToggle(a.id) : onOpen(a)),
          onLongPress: () => onToggle(a.id),
        });
        const waiting = a.components
          .map((c) => STATUS_OF[c.availability])
          .find((s): s is TileStatus => s !== undefined);
        return (
          <li key={a.id} className={`tile${isSelected ? " checked" : ""}`}>
            <button
              type="button"
              id={`tile-${a.id}`}
              className="tile-face"
              aria-pressed={isSelected}
              aria-label={label}
              onPointerDown={press.onPointerDown}
              onPointerUp={press.onPointerUp}
              onPointerMove={press.onPointerMove}
              onPointerCancel={press.onPointerCancel}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                  onToggle(a.id);
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  onOpen(a);
                }
              }}
            >
              {url ? (
                <img src={url} alt="" loading="lazy" decoding="async" />
              ) : (
                <span className="tile-blank" aria-hidden="true">
                  {a.kind === "VIDEO" ? (
                    <Video size={20} strokeWidth={1.5} />
                  ) : (
                    <Image size={20} strokeWidth={1.5} />
                  )}
                </span>
              )}
              {a.kind === "VIDEO" && url && (
                <span className="tile-play" aria-hidden="true">
                  <Play size={14} strokeWidth={2} fill="currentColor" />
                </span>
              )}
            </button>
            <input
              type="checkbox"
              className="tile-toggle"
              checked={isSelected}
              onChange={() => onToggle(a.id)}
              aria-label={`Select ${label}`}
            />
            <span className="tile-check" aria-hidden="true">
              {isSelected && <Check size={16} strokeWidth={3} />}
            </span>
            {waiting && (
              <span className="tile-status">
                {"ladder" in waiting ? (
                  <StatusPill state={waiting.ladder} />
                ) : (
                  <Pill tone={waiting.tone}>{waiting.label}</Pill>
                )}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
